import React, { FC, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { NavigateWithQuery } from '@dxb-web-ui-framework/common/components/NavigateWithQuery/NavigateWithQuery';
import { EligibleRoute, SecureRoute } from '../../../packages/iam/src/components';
import { AccountListProvider } from '../../../packages/account/src/contexts/AccountList/AccountListProvider';
import { CardActionsProvider } from '../../../packages/card/src/contexts/CardActions';
import { CardDetailsWrapper } from '../../../packages/card/src/contexts/CardDetails';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../packages/userProfile/src/contexts/UserInfo/hooks';

const CardDetailsPage = lazy(
  () => import(/* webpackChunkName: "CardDetailsPageComponent" */ '../../../packages/card/src/pages/CardDetailsPage'),
);

const CardTransactionDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ConsolidatedTransactionDetailsPageComponent" */ '../../../packages/card/src/pages/CardTransactionDetailsPage'
    ),
);

const TopUpCardPage = lazy(
  () => import(/* webpackChunkName: "TopUpCardPageComponent" */ '../../../packages/card/src/pages/TopUpCardPage'),
);

const TerminateCardPage = lazy(
  () =>
    import(/* webpackChunkName: "TerminateCardPageComponent" */ '../../../packages/card/src/pages/TerminateCardPage'),
);

const ReplaceCardPage = lazy(
  () => import(/* webpackChunkName: "ReplaceCardPageComponent" */ '../../../packages/card/src/pages/ReplaceCardPage'),
);

const PermanentBlockCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ReplaceCardPageComponent" */ '../../../packages/card/src/pages/PermanentBlockCardPage'
    ),
);

const ActivateCardPage = lazy(
  () => import(/* webpackChunkName: "ReplaceCardPageComponent" */ '../../../packages/card/src/pages/ActivateCardPage'),
);

const CardPinUnblockPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CardPinUnblockPagePageComponent" */ '../../../packages/card/src/pages/CardPinUnblockPage'
    ),
);

const CardActionsRouter: FC<GenericObject> = ({
  brandConfig,
  isCorporate,
  requiredEntitlements,
  logic,
  isCorporateAdminPage,
}) => {
  const location = useLocation();
  const customerId = useSelectedCustomerId();
  const isAllowed = useUserInfoIsUserAllowedAction(requiredEntitlements, logic);

  if (!isAllowed) {
    return <NavigateWithQuery to={`/${customerId}/dashboard`} replace state={{ from: location }} />;
  }

  return (
    <Routes>
      <Route
        path='/details'
        element={
          <SecureRoute requiredEntitlements={UserAppTransaction.PrepaidCardDetails}>
            <EligibleRoute>
              <CardDetailsWrapper>
                <CardActionsProvider>
                  <CardDetailsPage
                    pageConfig={brandConfig.cardDetailsPage}
                    isCorporateAdminPage={isCorporateAdminPage}
                  />
                </CardActionsProvider>
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/transaction/:transactionNo/details'
        element={
          <SecureRoute>
            <EligibleRoute>
              <CardDetailsWrapper>
                <CardTransactionDetailsPage pageConfig={brandConfig.cardTransactionDetailsPage} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/topup'
        element={
          <SecureRoute
            logic='OR'
            requiredEntitlements={[UserAppTransaction.PrepaidCardTopUp, UserAppTransaction.TopUpCorporateCards]}>
            <EligibleRoute>
              <CardDetailsWrapper shouldNotFetchCardTransactions>
                <TopUpCardPage pageConfig={brandConfig.topUpCardPage} isCorporate={isCorporate} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/terminate'
        element={
          <SecureRoute
            requiredEntitlements={[UserAppTransaction.CardTermination, UserAppTransaction.TerminateCorporateCards]}
            logic='OR'>
            <EligibleRoute>
              <AccountListProvider>
                <CardDetailsWrapper shouldNotFetchCardTransactions>
                  <TerminateCardPage pageConfig={brandConfig.terminateCardPage} isCorporate={isCorporate} />
                </CardDetailsWrapper>
              </AccountListProvider>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/replace'
        element={
          <SecureRoute
            requiredEntitlements={[UserAppTransaction.CardReplacement, UserAppTransaction.ReplaceCorporateCards]}
            logic='OR'>
            <EligibleRoute>
              <CardDetailsWrapper shouldNotFetchCardTransactions>
                <ReplaceCardPage pageConfig={brandConfig.replaceCardPage} isCorporate={isCorporate} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/permanent-block'
        element={
          <SecureRoute requiredEntitlements={UserAppTransaction.CardPermanentBlock}>
            <EligibleRoute>
              <CardDetailsWrapper>
                <PermanentBlockCardPage pageConfig={brandConfig.permanentBlockCardPage} isCorporate={isCorporate} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/reset-pin-attempts'
        element={
          <SecureRoute requiredEntitlements={UserAppTransaction.CardPinUnblock}>
            <EligibleRoute>
              <CardDetailsWrapper>
                <CardPinUnblockPage pageConfig={brandConfig.cardUnblockPage} isCorporate={isCorporate} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
      <Route
        path='/activate'
        element={
          <SecureRoute>
            <EligibleRoute>
              <CardDetailsWrapper shouldNotFetchCardTransactions>
                <ActivateCardPage pageConfig={brandConfig.activateCardPage} isCorporate={isCorporate} />
              </CardDetailsWrapper>
            </EligibleRoute>
          </SecureRoute>
        }
      />
    </Routes>
  );
};

export { CardActionsRouter };
